import { Typography } from '@mui/joy'
import Autocomplete from '@mui/joy/Autocomplete'
import CircularProgress from '@mui/joy/CircularProgress'
import { SavedAddress } from '@numeo/types-main-web-app'
import { SearchParams } from '@numeo/types-core'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    CityLocationData,
    StateLocationData,
    ZoneLocationData,
    LocationType,
    ZONE_STATES,
    locationDataSearchV2,
} from 'utils/location-search/LocationDataSearchV2'
import { MIN_CODES, parseCodeRun } from 'utils/location-search/parseCodeRun'
import { geocodeZip } from '../geocode'

/** A company saved address surfaced in the dropdown, tagged so the option guards can tell it apart. */
type SavedAddressOption = SavedAddress & { optionKind: 'savedAddress' }

/** Union type for location options (saved address, city, state, or zone) */
type LocationOption = CityLocationData | StateLocationData | ZoneLocationData | SavedAddressOption

/** "University Park, IL 04101" — US convention puts no comma before the ZIP. */
const savedPlaceLine = (option: SavedAddressOption): string => `${option.city}, ${option.state}${option.zip ? ` ${option.zip}` : ''}`

/**
 * The whole address on one line. Not what the row RENDERS — the row splits it over two lines to fit
 * a narrow field — but what getOptionLabel returns, which is what MUI anchors its auto-highlight on
 * and therefore what has to stay distinct from the plain city row's "City, ST".
 */
const savedAddressLine = (option: SavedAddressOption): string => [option.street, savedPlaceLine(option)].filter(Boolean).join(', ')

/** Type guard for company saved-address options — checked first, since they also carry a `city` */
const isSavedAddressOption = (option: LocationOption): option is SavedAddressOption => (option as SavedAddressOption).optionKind === 'savedAddress'

/** Type guard to distinguish city from state/zone */
const isCityOption = (option: LocationOption): option is CityLocationData =>
    !isSavedAddressOption(option) && 'city' in option && Boolean((option as CityLocationData).city)

/** Type guard for zone options */
const isZoneOption = (option: LocationOption): option is ZoneLocationData => 'zoneCode' in option && option.type === LocationType.ZONE

/**
 * The text the box already shows for the selected city — "University Park, IL". Both search paths key
 * off it and must agree on it: a city option is only ever selected with BOTH parts present (see the
 * defaultLocation effect), so a missing state means nothing is selected and there is no label to match.
 * Returns null in that case, which no query string can equal.
 */
const selectedCityLabel = (city?: string, stateProv?: string): string | null => (city && stateProv ? `${city}, ${stateProv}` : null)

/** Below this a prefix search matches most of the book, so the network call is not worth making. */
const SAVED_ADDRESS_MIN_QUERY = 2
/** Matches the debounce the deadhead address picker already uses. */
const SAVED_ADDRESS_DEBOUNCE_MS = 300

interface LocationSearchProps {
    onLocationSelect?: (location: CityLocationData) => void
    onStateSelect?: (state: StateLocationData) => void
    onZoneSelect?: (zone: ZoneLocationData) => void
    onBulkInput?: (items: (StateLocationData | ZoneLocationData)[]) => void
    includeStates?: boolean
    includeZones?: boolean
    placeholder?: string
    defaultLocation?: SearchParams['origin']
    defaultZoneCode?: string
    limit?: number
    isDisabled?: boolean
    onRemove?: () => void
    /** Called when Backspace is pressed with an empty input — lets parent remove its last chip */
    onRemoveLast?: () => void
    dataTestId?: string
    /** When true, renders listbox inline (disablePortal) to work inside MUI Dialog focus trap */
    insideDialog?: boolean
    /** When true, a city option shows ONLY the city name (no ", ST" suffix). Opt-in; default keeps the state. */
    hideStateInCity?: boolean
    /**
     * Optional source of company saved addresses, listed above the city results. Opt-in — without it
     * the dropdown behaves exactly as before, which is what every non-route consumer relies on.
     * Must be a stable reference (a module-level function), or the debounced effect re-runs forever.
     */
    searchSavedAddresses?: (query: string, signal?: AbortSignal) => Promise<SavedAddress[]>
    /** Called with the full saved address so the parent can fill street and ZIP alongside city/state. */
    onSavedAddressSelect?: (address: SavedAddress) => void
    /**
     * Optional remover for a saved address. When given, each saved row carries an ×.
     *
     * Receives the whole address, not just its id, so the consumer can name it in a confirmation —
     * and resolves false when the user backs out, which is what keeps the row on screen. Confirming
     * is the consumer's job: this component is shared by a dozen surfaces and has no business owning
     * an app-specific dialog.
     */
    onDeleteSavedAddress?: (address: SavedAddress) => Promise<boolean> | boolean
}

export const LocationSearch: React.FC<LocationSearchProps> = ({
    onLocationSelect,
    onStateSelect,
    onZoneSelect,
    onBulkInput,
    includeStates = false,
    includeZones = false,
    hideStateInCity = false,
    defaultLocation = {} as SearchParams['origin'],
    defaultZoneCode,
    placeholder = 'Search for a city',
    isDisabled = false,
    onRemove,
    onRemoveLast,
    dataTestId,
    insideDialog = false,
    searchSavedAddresses,
    onSavedAddressSelect,
    onDeleteSavedAddress,
}) => {
    const [open, setOpen] = useState(false)
    const [cityOptions, setCityOptions] = useState<LocationOption[]>([])
    const inputValueRef = useRef('')
    const [inputValue, setInputValue] = useState('')
    const [savedOptions, setSavedOptions] = useState<SavedAddressOption[]>([])
    // Ids removed during this mount. A lookup that left the server before the delete still resolves
    // afterwards and would otherwise put the row back, since the response carries no knowledge of it.
    const deletedSavedIdsRef = useRef<Set<string>>(new Set())
    const [loading, setLoading] = useState(false)
    const [value, setValue] = useState<LocationOption | null>(null)
    // Monotonic id per handleSearch call. The ZIP branch awaits a network geocode (the local-dataset
    // branches are effectively instant), so a slow response for an EARLIER input can resolve after the
    // user kept typing — a stale setCityOptions would clobber the newer, already-rendered list. Each search
    // claims a new id and only the latest is allowed to commit its results.
    const searchSeqRef = useRef(0)
    /** Derived once so the saved-address effect and handleSearch cannot drift apart on what "already selected" means. */
    const currentCityLabel = selectedCityLabel(defaultLocation?.city, defaultLocation?.stateProv)

    useEffect(() => {
        if (defaultZoneCode && ZONE_STATES[defaultZoneCode]) {
            setValue({
                id: `zone-${defaultZoneCode}`,
                zoneCode: defaultZoneCode,
                states: ZONE_STATES[defaultZoneCode],
                type: LocationType.ZONE,
            } as ZoneLocationData)
        } else if (defaultLocation?.city && defaultLocation?.stateProv) {
            setValue({
                city: defaultLocation.city,
                stateCode: defaultLocation.stateProv,
                latitude: defaultLocation.latitude,
                longitude: defaultLocation.longitude,
            } as CityLocationData)
        } else if (includeStates && !defaultLocation?.city && defaultLocation?.stateProv) {
            setValue({
                id: `state-${defaultLocation.stateProv}`,
                stateCode: defaultLocation.stateProv,
                stateName: '',
                type: LocationType.STATE,
            } as StateLocationData)
        } else {
            setValue(null)
        }
    }, [defaultLocation, defaultZoneCode, includeStates])

    useEffect(() => {
        if (!searchSavedAddresses) return

        const query = inputValue.trim()
        // Re-searching the label of the address already selected would only ever return nothing,
        // since the label carries the state ("University Park, IL") and the book indexes the city.
        const isCurrentSelectionLabel = query === currentCityLabel

        if (query.length < SAVED_ADDRESS_MIN_QUERY || isCurrentSelectionLabel) {
            setSavedOptions([])
            return
        }

        const controller = new AbortController()
        // Drop the previous query's rows BEFORE waiting on this one. Aborting the request does not
        // touch state, and the city list has no such delay — it resolves off the local dataset almost
        // immediately — so leaving them would put suggestions for a query that is no longer in the box
        // at the top of a list of fresh city rows, where autoHighlight can land on one and ENTER can
        // write a completely different street, city and ZIP into the stop.
        setSavedOptions([])

        const timer = setTimeout(() => {
            void searchSavedAddresses(query, controller.signal)
                .then((results) => {
                    if (controller.signal.aborted) return
                    setSavedOptions(
                        results
                            .filter((address) => !deletedSavedIdsRef.current.has(address.id))
                            .map((address) => ({ ...address, optionKind: 'savedAddress' as const }))
                    )
                })
                // searchSavedAddresses is a prop, so a consumer's implementation may reject even
                // though the one shipped here never does. Suggestions are optional — drop them and
                // leave the city results alone rather than surfacing an unhandled rejection.
                .catch(() => {
                    if (!controller.signal.aborted) setSavedOptions([])
                })
        }, SAVED_ADDRESS_DEBOUNCE_MS)

        return () => {
            clearTimeout(timer)
            controller.abort()
        }
    }, [inputValue, searchSavedAddresses, currentCityLabel])

    const handleSearch = async (query: string) => {
        // Skip search if input matches current value display
        if (query === currentCityLabel) {
            return
        }
        if (includeStates && !defaultLocation?.city && defaultLocation?.stateProv && query === defaultLocation.stateProv) {
            return
        }
        if (defaultZoneCode && ZONE_STATES[defaultZoneCode]) {
            const zoneLabel = `${defaultZoneCode} (${ZONE_STATES[defaultZoneCode]})`
            if (query === zoneLabel || query === defaultZoneCode) return
        }

        const seq = ++searchSeqRef.current
        const isStale = () => seq !== searchSeqRef.current
        setLoading(true)
        try {
            const results: LocationOption[] = []
            const trimmed = query.trim()

            // A 5-digit US ZIP isn't in the local city dataset, so resolve it live to a city/state point and offer
            // it as a single option — selecting it flows through the same city path (onLocationSelect) as a typed
            // city, so callers need no ZIP-specific handling.
            if (/^\d{5}$/.test(trimmed)) {
                const place = await geocodeZip(trimmed)
                if (isStale()) return
                if (place) {
                    results.push({
                        id: `zip-${trimmed}`,
                        type: LocationType.CITY,
                        city: place.city || trimmed,
                        stateCode: place.stateCode,
                        stateName: '',
                        county: '',
                        latitude: place.lat,
                        longitude: place.lng,
                        postalCode: trimmed,
                    })
                }
                setCityOptions(results)
                return
            }

            // Search zones for queries starting with "Z"
            if (includeZones && trimmed.toUpperCase().startsWith('Z')) {
                const zones = locationDataSearchV2.searchByZoneCodes(trimmed)
                results.push(...zones)
            }

            // Search states for short queries (1-2 chars) that don't start with Z
            if (includeStates && trimmed.length >= 1 && trimmed.length <= 2) {
                const states = locationDataSearchV2.searchByStateCodes(trimmed)
                results.push(...states)
            }

            // Search cities for longer queries (>2 chars)
            if (trimmed.length > 2 && !trimmed.toUpperCase().startsWith('Z')) {
                // Split "chicago il" → ["chicago", "il"] to handle multi-token queries
                // First try the full query, then if tokens include a 2-char state code,
                // search by city part and filter by state for better results.
                const tokens = trimmed.split(/[\s,]+/).filter((t) => t.length > 0)
                const cities = await locationDataSearchV2.search(trimmed)

                if (cities.length > 0) {
                    results.push(...cities)
                } else if (tokens.length >= 2) {
                    // No results for full query — try searching by the longest token (city name)
                    // and filter by the short token (state code) if present
                    const longTokens = tokens.filter((t) => t.length > 2)
                    const shortTokens = tokens.filter((t) => t.length <= 2)
                    const cityQuery = longTokens.join(' ')
                    if (cityQuery.length > 2) {
                        const cityResults = await locationDataSearchV2.search(cityQuery)
                        if (shortTokens.length > 0) {
                            const stateFilter = shortTokens[0].toUpperCase()
                            const filtered = cityResults.filter((c) => c.stateCode.toUpperCase() === stateFilter)
                            results.push(...(filtered.length > 0 ? filtered : cityResults))
                        } else {
                            results.push(...cityResults)
                        }
                    }
                }

                // Also include state results if any token matches a state code
                if (includeStates) {
                    const stateTokens = tokens.filter((t) => t.length <= 2)
                    for (const st of stateTokens) {
                        const states = locationDataSearchV2.searchByStateCodes(st)
                        results.push(...states)
                    }
                }
            }

            if (isStale()) return
            setCityOptions(results)
        } catch (error) {
            console.error('Search failed:', error)
            if (!isStale()) setCityOptions([])
        } finally {
            // Only the LATEST search owns the spinner — a stale one finishing must not hide it while the
            // newer request is still in flight.
            if (!isStale()) setLoading(false)
        }
    }

    /** Validate a single uppercase 2-char token as a state or zone, return the match or null */
    const matchToken = (token: string): StateLocationData | ZoneLocationData | null => {
        if (token.startsWith('Z') && token.length === 2) {
            const zones = locationDataSearchV2.searchByZoneCodes(token)
            if (zones.length === 1) return zones[0]
        }
        if (token.length === 2) {
            const states = locationDataSearchV2.searchByStateCodes(token)
            if (states.length === 1) return states[0]
        }
        return null
    }

    /**
     * Whether the dropdown is already offering a city that the typed text is a prefix of —
     * "WACO" → Waco, TX. Such a run must NOT be read as a state list even though WA + CO both
     * resolve: the dispatcher is mid-way through typing a city and ENTER belongs to the
     * highlighted row. Same guard clears Denver (DE+NV), Vail (VA+IL) and Alma (AL+MA).
     *
     * `cityOptions` may still hold the results of a slightly shorter query when ENTER lands
     * fast, and that is the safe direction: options fetched for "WAC" still contain Waco, which
     * prefix-matches "WACO", so the guard trips. Options for "GAF" contain no city starting
     * with "GAFL", so a genuine state run is never blocked.
     */
    const hasCityPrefixMatch = (input: string): boolean => {
        const needle = input.toLowerCase()
        return cityOptions.some((option) => isCityOption(option) && option.city.toLowerCase().startsWith(needle))
    }

    /** Try to parse multi-token state/zone input and call onBulkInput.
     *  Returns true if the input was fully handled (caller should preventDefault). */
    const handleMultiInput = (input: string): boolean => {
        if (!onBulkInput) return false
        const tokens = input
            .split(/[\s,]+/)
            .map((t) => t.trim().toUpperCase())
            .filter((t) => t.length > 0)

        const accept = (results: (StateLocationData | ZoneLocationData)[]): true => {
            onBulkInput(results)
            setValue(null)
            setCityOptions([])
            setOpen(false)
            return true
        }

        // --- Space/comma-delimited tokens ("GA, FL" / "GA FL XX") ---
        if (tokens.length >= 2) {
            // Unknown codes are only skippable when EVERY token is code-shaped. "chicago il" has a
            // 7-char token, so it stays all-or-nothing and falls through to the city search rather
            // than resolving to a lone IL chip.
            const allCodeShaped = tokens.every((token) => token.length === 2)
            const results: (StateLocationData | ZoneLocationData)[] = []
            for (const token of tokens) {
                const match = matchToken(token)
                if (match) results.push(match)
                else if (!allCodeShaped) return false
            }
            if (results.length >= MIN_CODES) return accept(results)
            return false
        }

        // --- Single no-delimiter run: "GAFL", "FLGAIL", "Z0Z1Z2" ---
        if (tokens.length === 1) {
            if (hasCityPrefixMatch(tokens[0])) return false
            const run = parseCodeRun(tokens[0], matchToken)
            if (run) return accept(run)
        }

        return false
    }

    /** Ensure current value is always present in options to avoid MUI warning */
    const effectiveOptions = useMemo(() => {
        // Saved addresses lead: a place the company has actually used beats a generic city match.
        const options = savedOptions.length > 0 ? [...savedOptions, ...cityOptions] : cityOptions
        if (!value) return options
        const valueExists = options.some((opt) => {
            if (isSavedAddressOption(opt) || isSavedAddressOption(value)) {
                return isSavedAddressOption(opt) && isSavedAddressOption(value) && opt.id === value.id
            }
            if (isZoneOption(opt) && isZoneOption(value)) {
                return opt.zoneCode === value.zoneCode
            }
            if (isCityOption(opt) && isCityOption(value)) {
                return opt.city === value.city && opt.stateCode === value.stateCode
            }
            if (!isCityOption(opt) && !isZoneOption(opt) && !isCityOption(value) && !isZoneOption(value)) {
                return (opt as StateLocationData).stateCode === (value as StateLocationData).stateCode
            }
            return false
        })
        if (!valueExists) return [value, ...options]
        return options
    }, [cityOptions, savedOptions, value])

    /**
     * Hand the address to the consumer and drop the row only if it says the delete happened. The
     * consumer confirms with the user first, so a cancelled dialog resolves false and leaves the row
     * exactly where it was.
     */
    const requestDeleteSaved = async (address: SavedAddressOption) => {
        if (!onDeleteSavedAddress) return
        // Close the listbox first. It floats at z-index 10001 so that it clears the sidebar it opens
        // inside, which also puts it above any dialog the consumer raises to confirm — leaving the
        // dropdown open would cover the very question being asked.
        setOpen(false)
        const { optionKind, ...saved } = address
        void optionKind
        const deleted = await onDeleteSavedAddress(saved)
        if (!deleted) return
        deletedSavedIdsRef.current.add(address.id)
        setSavedOptions((current) => current.filter((option) => option.id !== address.id))
    }

    const getOptionLabel = (option: LocationOption): string => {
        // The FULL address line, deliberately different from the plain city row's "City, ST".
        //
        // MUI re-anchors the auto-highlight by label (useAutocomplete's getPreviousHighlightedOptionIndex).
        // Saved rows always arrive ~300ms after the local city list and are prepended, so if a saved
        // row carried the same label as the highlighted city row, the highlight would silently move
        // onto it and ENTER would mean something different depending on how fast the dispatcher typed.
        // With a distinct label the highlight follows the city row to its new index, and picking the
        // saved address stays an explicit act.
        //
        // This text never reaches the "City & State" input: onChange below immediately replaces the
        // value with a city-shaped one, and MUI resets the input from THAT label.
        if (isSavedAddressOption(option)) return savedAddressLine(option)
        if (isZoneOption(option)) return `${option.zoneCode} (${option.states})`
        // Always includes the state — this drives the SELECTED value's input text, which must stay
        // unambiguous (which Richmond?). The dropdown ROW uses a city-only left label separately (renderOption).
        if (isCityOption(option)) return `${option.city}, ${option.stateCode}`
        const state = option as StateLocationData
        return state.stateName && state.stateName !== state.stateCode ? `${state.stateCode} - ${state.stateName}` : state.stateCode
    }

    return (
        <div
            className="w-full h-full"
            onKeyDownCapture={(e) => {
                if (e.key === 'Enter' && inputValueRef.current) {
                    const raw = inputValueRef.current.trim()
                    if (handleMultiInput(raw)) {
                        e.preventDefault()
                        e.stopPropagation()
                    }
                }
                if (e.key === 'Backspace' && !inputValueRef.current && !value && onRemoveLast) {
                    e.preventDefault()
                    onRemoveLast()
                }
                if (e.key === 'Tab' && open) {
                    const focused = document.querySelector('.MuiAutocomplete-listbox .Mui-focused, .MuiAutocomplete-listbox .Mui-focusVisible') as HTMLElement
                    if (focused) {
                        focused.click()
                        e.preventDefault()
                    }
                }
            }}
        >
            <Autocomplete
                name="origin"
                filterOptions={(opts) => opts}
                value={value}
                onChange={(_, newValue, reason) => {
                    if (newValue && isSavedAddressOption(newValue)) {
                        // Hold a city-shaped value rather than the address itself, so the input reads
                        // "University Park, IL" and stays consistent with what the parent's
                        // defaultLocation feeds back on the next render.
                        setValue({
                            city: newValue.city,
                            stateCode: newValue.state,
                            latitude: newValue.coordinates?.coordinates[1],
                            longitude: newValue.coordinates?.coordinates[0],
                        } as CityLocationData)
                        const { optionKind, ...address } = newValue
                        void optionKind
                        onSavedAddressSelect?.(address)
                        return
                    }

                    setValue(newValue)
                    if (newValue) {
                        if (isZoneOption(newValue)) {
                            onZoneSelect?.(newValue)
                        } else if (isCityOption(newValue)) {
                            onLocationSelect?.(newValue)
                        } else {
                            onStateSelect?.(newValue as StateLocationData)
                        }
                    } else {
                        if (onRemove && (reason === 'clear' || reason === 'removeOption')) {
                            onRemove()
                        }
                    }
                }}
                disabled={isDisabled}
                sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    minWidth: 0,
                    maxWidth: '100%',
                    border: 'none',
                    borderRadius: 0,
                    boxShadow: 'none',
                    padding: 0,
                    backgroundColor: 'transparent',
                    '--Input-focusedInsetShadow': 'none',
                    '--Input-focusedThickness': '0px',
                    '&:hover': { backgroundColor: 'transparent' },
                    '&::before': { display: 'none' },
                    '& .MuiAutocomplete-root': { width: '100%' },
                    '& .MuiAutocomplete-wrapper': { margin: 0, gap: 0 },
                    '& .MuiAutocomplete-listbox': { maxWidth: '100%' },
                    '& .MuiAutocomplete-input': { width: '100%', padding: 0 },
                    '& .MuiAutocomplete-endDecorator': { marginRight: 0 },
                }}
                placeholder={placeholder}
                open={open}
                onOpen={() => {
                    setOpen(true)
                    handleSearch('') // Load all options when opening
                }}
                onClose={() => setOpen(false)}
                onInputChange={(_, val) => {
                    inputValueRef.current = val
                    // Only the saved-address effect reads this, and that effect no-ops without a
                    // source. Guarding the write leaves every consumer that does not opt in on
                    // exactly the render count it had before this prop existed.
                    if (searchSavedAddresses) setInputValue(val)
                    handleSearch(val)
                }}
                loading={loading}
                options={effectiveOptions}
                isOptionEqualToValue={(option, val) => {
                    if (isSavedAddressOption(option) || isSavedAddressOption(val)) {
                        return isSavedAddressOption(option) && isSavedAddressOption(val) && option.id === val.id
                    }
                    if (isZoneOption(option) && isZoneOption(val)) return option.zoneCode === val.zoneCode
                    if (isCityOption(option) && isCityOption(val)) return option.city === val.city && option.stateCode === val.stateCode
                    if (!isCityOption(option) && !isZoneOption(option) && !isCityOption(val) && !isZoneOption(val)) {
                        return (option as StateLocationData).stateCode === (val as StateLocationData).stateCode
                    }
                    return false
                }}
                getOptionLabel={getOptionLabel}
                slotProps={{
                    listbox: {
                        ...(insideDialog ? { disablePortal: true } : {}),
                        sx: {
                            zIndex: 10001,
                            py: 0.5,
                            '& .Mui-focused, & .Mui-focusVisible, & [aria-selected="true"]': {
                                backgroundColor: 'var(--joy-palette-primary-softBg, #E0E7FF) !important',
                            },
                            // The remove control is noise on every row at rest, so it only appears on
                            // the row being pointed at — or highlighted, which is what makes it visible
                            // to someone navigating with the arrow keys rather than a mouse.
                            '& .location-saved-row .location-saved-remove': { visibility: 'hidden' },
                            '& .location-saved-row:hover .location-saved-remove, & .location-saved-row.Mui-focused .location-saved-remove, & .location-saved-row.Mui-focusVisible .location-saved-remove':
                                { visibility: 'visible' },
                            '& .location-saved-remove:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                        },
                    },
                    input: { 'data-testid': dataTestId },
                }}
                renderOption={(props, option) => {
                    // Create a new props object without ownerState to prevent React warning
                    const { ownerState, ...otherProps } = props as React.HTMLAttributes<HTMLLIElement> & { ownerState?: unknown }
                    void ownerState
                    const isFocused = /Mui-focused|focusVisible/.test(otherProps.className || '')
                    // In city-only (hideStateInCity) mode the label drops the state, so same-named cities
                    // (Richmond VA / CA / IN…) would look identical — surface the state code on the right
                    // (replacing the "City" tag) to keep them distinguishable without cluttering the name.
                    const typeLabel = isSavedAddressOption(option)
                        ? 'Saved'
                        : isZoneOption(option)
                          ? 'Zone'
                          : isCityOption(option)
                            ? hideStateInCity
                                ? option.stateCode
                                : 'City'
                            : 'State'
                    // A saved row shows the whole address, since the street and ZIP are exactly what
                    // distinguishes it from the plain city row sitting below it.
                    const rowLabel = isSavedAddressOption(option)
                        ? savedAddressLine(option)
                        : hideStateInCity && isCityOption(option)
                          ? option.city
                          : getOptionLabel(option)
                    const saved = isSavedAddressOption(option) ? option : null
                    const deletable = Boolean(onDeleteSavedAddress) && saved !== null
                    return (
                        <li
                            {...otherProps}
                            className={[otherProps.className, saved ? 'location-saved-row' : ''].filter(Boolean).join(' ')}
                            style={{
                                cursor: 'pointer',
                                padding: '6px 12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '8px',
                                ...(isFocused ? { backgroundColor: 'var(--joy-palette-primary-softBg, #E0E7FF)' } : {}),
                            }}
                            key={saved ? `saved-${saved.id}` : isZoneOption(option) ? option.zoneCode : option.id}
                            // Lets a test (and anything reading the DOM) tell a saved row from a city row.
                            data-saved-id={saved ? saved.id : undefined}
                        >
                            {saved ? (
                                // The street and its controls share the first line; the place sits under
                                // it. flex: 1 on this column is what keeps "Universal City, CA 33430" on
                                // one line instead of stranding the ZIP on a third.
                                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                        <Typography sx={{ lineHeight: 1.3 }}>{saved.street || savedPlaceLine(saved)}</Typography>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: '12px', color: 'text.tertiary' }}>{typeLabel}</Typography>
                                            {deletable ? (
                                                // aria-hidden with no role: ARIA forbids interactive
                                                // descendants inside an option, and a nested button makes a
                                                // screen reader announce the row as activatable two ways.
                                                <span
                                                    className="location-saved-remove"
                                                    aria-hidden="true"
                                                    data-testid="saved-address-remove"
                                                    // mousedown, not click: MUI selects the option on
                                                    // mousedown, so stopping it there is what keeps the ×
                                                    // from also picking the row.
                                                    onMouseDown={(event) => {
                                                        event.preventDefault()
                                                        event.stopPropagation()
                                                    }}
                                                    onClick={(event) => {
                                                        event.preventDefault()
                                                        event.stopPropagation()
                                                        void requestDeleteSaved(saved)
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        // A real hit target rather than a bare glyph, which
                                                        // is a ~7px wide thing to aim at sitting one slip
                                                        // away from selecting the row. The circle is also
                                                        // what separates it from the tag beside it.
                                                        width: '22px',
                                                        height: '22px',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '16px',
                                                        lineHeight: 1,
                                                        color: 'var(--joy-palette-text-tertiary, #9AA0A6)',
                                                    }}
                                                >
                                                    ×
                                                </span>
                                            ) : null}
                                        </span>
                                    </span>
                                    {saved.street ? (
                                        <Typography sx={{ fontSize: '12px', color: 'text.tertiary', lineHeight: 1.3 }}>
                                            {saved.city}, {saved.state}
                                        </Typography>
                                    ) : null}
                                    {/* The ZIP gets its own line rather than trailing the state, so the
                                        row reads as an address block: street, then place, then ZIP. */}
                                    {saved.street && saved.zip ? (
                                        <Typography sx={{ fontSize: '12px', color: 'text.tertiary', lineHeight: 1.3 }}>{saved.zip}</Typography>
                                    ) : null}
                                </span>
                            ) : (
                                <>
                                    <Typography>{rowLabel}</Typography>
                                    <Typography sx={{ fontSize: '12px', color: 'text.tertiary', flexShrink: 0 }}>{typeLabel}</Typography>
                                </>
                            )}
                        </li>
                    )
                }}
                autoHighlight
                endDecorator={loading ? <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} /> : null}
            />
        </div>
    )
}
